import { Helmet } from 'react-helmet-async';
// @mui
import {
  Box,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
// components
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import Iconify from '../../components/iconify';
import moment from 'moment';
import {
  GET_ALL_MULAN_FCT_WEEK_REGISTRATIONS,
  GET_MULAN_FCT_WEEK_REGISTRATION_BY_ID,
  RESOLVE_MULAN_FCT_WEEK_PAYMENT,
} from '../../graphql/queries/mulanfctweek';

const CATEGORY_LABELS = {
  '0-5': '0–5 years post-call',
  '6-10': '6–10 years post-call',
  '11-20': '11–20 years post-call',
  'above-20': 'Above 20 years post-call',
  'san-bencher': 'Senior Advocates of Nigeria (SANs) and Benchers',
  'guest': 'Guests',
};

// ----------------------------------------------------------------------

export default function AdminMulanFctWeekBookings() {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [status, setStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentResolutionData, setPaymentResolutionData] = useState({
    transaction_id: '',
    type: 'MulanFctWeekRegistration',
    gateway: 'paystack',
    tx_ref: '',
    status: 'success',
    serviceAmount: 0,
  });

  const { data, loading, refetch } = useQuery(GET_ALL_MULAN_FCT_WEEK_REGISTRATIONS, {
    variables: { page, pageSize, status, searchTerm },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const [getRegistrationById] = useLazyQuery(
    GET_MULAN_FCT_WEEK_REGISTRATION_BY_ID,
    {
      onCompleted: (data) => {
        if (data?.getMulanFctWeekRegistrationById) {
          const registration = data.getMulanFctWeekRegistrationById;
          setSelectedRegistration(registration);
          // Initialize payment resolution data when dialog opens
          setPaymentResolutionData({
            transaction_id: registration._id,
            type: 'MulanFctWeekRegistration',
            gateway: 'paystack',
            tx_ref: '',
            status: 'success',
            serviceAmount: registration.amount || 0,
          });
          setDialogOpen(true);
        }
      },
      onError: (error) => {
        enqueueSnackbar(error.message || 'Failed to fetch registration details', { variant: 'error' });
      },
    }
  );

  const [
    resolvePaymentStatus,
    { loading: resolvePaymentStatusLoading, error: resolvePaymentStatusError, data: resolvePaymentStatusData },
  ] = useMutation(RESOLVE_MULAN_FCT_WEEK_PAYMENT);

  const registrations = data?.getAllMulanFctWeekRegistrations?.registrations || [];
  const pageInfo = data?.getAllMulanFctWeekRegistrations?.pageInfo || {
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handleSearchDebounced = debounce(async (newSearchTerm) => {
    const newPage = 1;
    setPage(newPage);
    setSearchTerm(newSearchTerm);
    setSearchLoading(true);
    await refetch({
      page: newPage,
      pageSize,
      status,
      searchTerm: newSearchTerm,
    });
    setSearchLoading(false);
  }, 300);

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setSearchLoading(false);
    handleSearchDebounced(newSearchTerm);
  };

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    const newPage = 1;
    setStatus(newStatus);
    setPage(newPage);
    await refetch({
      page: newPage,
      pageSize,
      status: newStatus,
      searchTerm,
    });
  };

  const handlePageChange = async (newPage) => {
    setPage(newPage);
    await refetch({
      page: newPage,
      pageSize,
      status,
      searchTerm,
    });
  };

  const handleViewDetails = (id) => {
    getRegistrationById({ variables: { id } });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRegistration(null);
    // Reset payment resolution data
    setPaymentResolutionData({
      transaction_id: '',
      type: 'MulanFctWeekRegistration',
      gateway: 'paystack',
      tx_ref: '',
      status: 'success',
      serviceAmount: 0,
    });
  };

  const handleResolvePaymentStatus = async () => {
    if (!selectedRegistration) return;

    try {
      await resolvePaymentStatus({
        variables: {
          input: {
            registrationId: selectedRegistration._id,
            transactionReference: paymentResolutionData.tx_ref,
            amount: parseInt(paymentResolutionData.serviceAmount),
          },
        },
      });
    } catch (error) {
      // Error will be handled by useEffect
      console.error('Error resolving payment:', error);
    }
  };

  // Handle payment resolution success
  useEffect(() => {
    if (resolvePaymentStatusData) {
      enqueueSnackbar('Payment resolved successfully!', { variant: 'success' });
      // Refetch the list to show updated status
      refetch();
      // Close the dialog after a short delay to show the success message
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    }
  }, [resolvePaymentStatusData, refetch, enqueueSnackbar]);

  // Handle payment resolution error
  useEffect(() => {
    if (resolvePaymentStatusError) {
      enqueueSnackbar(
        resolvePaymentStatusError.message || 'Failed to resolve payment',
        { variant: 'error' }
      );
    }
  }, [resolvePaymentStatusError, enqueueSnackbar]);

  return (
    <>
      <Helmet>
        <title> MULAN FCT Week Bookings | BeeBot </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            MULAN FCT Week Bookings
          </Typography>
        </Stack>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TextField
              value={searchTerm}
              fullWidth
              placeholder="Search by name, email, mobile, SCN, or receipt number..."
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchLoading && <CircularProgress size={20} />}
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Select
              value={status}
              onChange={handleStatusChange}
              fullWidth
              displayEmpty
              inputProps={{ 'aria-label': 'Status' }}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </Grid>
        </Grid>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>SCN</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : registrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No registrations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.map((registration) => (
                    <TableRow key={registration._id} hover>
                      <TableCell>{registration.name}</TableCell>
                      <TableCell>{registration.email}</TableCell>
                      <TableCell>{registration.mobile}</TableCell>
                      <TableCell>{registration.scn || 'N/A'}</TableCell>
                      <TableCell>
                        {CATEGORY_LABELS[registration.category] || registration.category}
                      </TableCell>
                      <TableCell>₦{registration.amount?.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={registration.status || 'pending'}
                          color={registration.status === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {moment(registration.created_at).format('MMM DD, YYYY')}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(registration._id)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {pageInfo.currentPage} of {pageInfo.totalPages} ({pageInfo.totalItems} total)
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => handlePageChange(page - 1)}
                disabled={!pageInfo.hasPrevPage || loading}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                onClick={() => handlePageChange(page + 1)}
                disabled={!pageInfo.hasNextPage || loading}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Details Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>Registration Details</DialogTitle>
          <DialogContent>
            {selectedRegistration && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{selectedRegistration.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{selectedRegistration.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Mobile
                  </Typography>
                  <Typography variant="body1">{selectedRegistration.mobile}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    WhatsApp
                  </Typography>
                  <Typography variant="body1">{selectedRegistration.whatsapp}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    SCN
                  </Typography>
                  <Typography variant="body1">{selectedRegistration.scn || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {CATEGORY_LABELS[selectedRegistration.category] || selectedRegistration.category}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1">₦{selectedRegistration.amount?.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedRegistration.status || 'pending'}
                    color={selectedRegistration.status === 'paid' ? 'success' : 'warning'}
                    size="small"
                  />
                </Grid>
                {selectedRegistration.receiptNumber && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary">
                      Receipt Number
                    </Typography>
                    <Typography variant="body1">{selectedRegistration.receiptNumber}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="overline" color="text.secondary">
                    Registration Date
                  </Typography>
                  <Typography variant="body1">
                    {moment(selectedRegistration.created_at).format('MMMM DD, YYYY hh:mm A')}
                  </Typography>
                </Grid>
                {!selectedRegistration.paymentDetails && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Payment Resolution
                      </Typography>
                      <TextField
                        fullWidth
                        label="Transaction Reference"
                        value={paymentResolutionData.tx_ref}
                        onChange={(e) =>
                          setPaymentResolutionData({ ...paymentResolutionData, tx_ref: e.target.value })
                        }
                        variant="outlined"
                        margin="normal"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentResolutionData.serviceAmount}
                        onChange={(e) =>
                          setPaymentResolutionData({
                            ...paymentResolutionData,
                            serviceAmount: e.target.value,
                          })
                        }
                        variant="outlined"
                        margin="normal"
                        size="small"
                      />
                      <Stack spacing={2} direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
                        <Button
                          onClick={handleResolvePaymentStatus}
                          variant="contained"
                          disabled={resolvePaymentStatusLoading || !paymentResolutionData.tx_ref}
                        >
                          {resolvePaymentStatusLoading ? 'Resolving...' : 'Resolve Payment'}
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

